import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { SendForBiddingDialogComponent } from '../send-for-bidding-dialog/send-for-bidding-dialog.component';
import { BiddingLinkDialogComponent } from '../bidding-link-dialog/bidding-link-dialog.component';
import { InvitationsListDialogComponent } from '../invitations-list-dialog/invitations-list-dialog.component';
import { toPublicAppUrl } from '../public-app-url';
import { AiReportDialogService } from 'src/app/shared/services/ai-report-dialog.service';

export interface BoQ {
  boq_id: number;
  boq_code: string;
  boq_name: string;
  description: string;
  project_id: number;
  project_name: string;
  status: string;
  estimation_status?: string;
  assigned_to?: number;
  assigned_to_name?: string;
  total_amount: number;
  currency: string;
  version: number;
  created_at: string;
  created_by_name: string;
  item_count: number;
}

@Component({
  selector: 'app-boq-list',
  templateUrl: './boq-list.component.html',
  styleUrls: ['./boq-list.component.css']
})
export class BoqListComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'boq_code',
    // 'boq_name',
    'project_name',
    'assigned_to_name',
    'estimation_status',
    'status',
    'total_amount',
    'item_count',
    'created_at',
    'actions'
  ];

  dataSource = new MatTableDataSource<BoQ>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;
  searchKeyword = '';
  selectedStatus: string = '';
  selectedProject: string = '';
  selectedEstimationStatus: string = '';
  assignedToMe = false;
  projects: any[] = [];

  // Pagination
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 20, 50];

  statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'in_review', label: 'In Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'archived', label: 'Archived' }
  ];

  estimationStatusOptions = [
    { value: '', label: 'All estimation' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending_review', label: 'Pending review' },
    { value: 'changes_requested', label: 'Changes requested' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  constructor(
    private apiService: ApiService,
    private toastrService: ToastrMsgService,
    private router: Router,
    private dialog: MatDialog,
    private aiReportDialog: AiReportDialogService
  ) {}

  ngOnInit() {
    this.loadProjects();
    this.loadBoQs();
  }

  openAiSummary(boq: BoQ) {
    this.aiReportDialog.open({
      type: 'boq_summary',
      preset: { projectId: boq.project_id, boqId: boq.boq_id },
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  loadBoQs() {
    this.loading = true;
    const params: any = {
      page: this.pageIndex + 1,
      limit: this.pageSize
    };

    if (this.searchKeyword) {
      params.search = this.searchKeyword;
    }

    if (this.selectedStatus) {
      params.status = this.selectedStatus;
    }

    if (this.selectedProject) {
      params.project_id = this.selectedProject;
    }
    if (this.selectedEstimationStatus) {
      params.estimation_status = this.selectedEstimationStatus;
    }
    if (this.assignedToMe) {
      params.assigned_to = 'me';
    }

    this.apiService.getBoQs(params).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.dataSource.data = res.data;
          this.totalItems = res.pagination?.total || 0;
        } else {
          this.toastrService.error('Failed to load BoQs');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading BoQs:', err);
        this.toastrService.error(err.error?.error || 'Failed to load BoQs');
        this.loading = false;
      }
    });
  }

  applyFilter() {
    this.pageIndex = 0;
    this.loadBoQs();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadBoQs();
  }

  formatCurrency(amount: number, currency: string = 'INR'): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'draft': 'bg-gray-100 text-gray-800',
      'in_review': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'archived': 'bg-gray-100 text-gray-600'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  viewBoQ(boq: BoQ) {
    this.router.navigate(['/home/boq', boq.boq_id, 'view']);
  }

  openDeviation(boq: BoQ) {
    this.router.navigate(['/home/boq', boq.boq_id, 'deviation']);
  }

  editBoQ(boq: BoQ) {
    this.router.navigate(['/home/boq', boq.boq_id, 'edit']);
  }

  createBoQ() {
    this.router.navigate(['/home/boq', 'new']);
  }

  viewBids(boq: BoQ) {
    this.openInvitationsDialog(boq);
  }

  sendForBidding(boq: BoQ) {
    if (!this.canSendForBidding(boq)) {
      this.toastrService.error('Approve estimation before publishing this enquiry');
      return;
    }
    this.apiService.getBiddingInvitationsByBoq(boq.boq_id).subscribe({
      next: (res: any) => {
        const invitations = (res?.data || []).filter((inv: any) => inv.status !== 'draft');
        if (invitations.length) {
          this.openInvitationsDialog(boq, true);
          return;
        }
        this.openSendForBiddingDialog(boq);
      },
      error: () => {
        this.openSendForBiddingDialog(boq);
      }
    });
  }

  private openInvitationsDialog(boq: BoQ, focusInviteMore = false) {
    this.dialog.open(InvitationsListDialogComponent, {
      width: '760px',
      maxWidth: '94vw',
      autoFocus: false,
      data: {
        boq_id: boq.boq_id,
        boq_code: boq.boq_code,
        boq_name: boq.boq_name,
        focusInviteMore
      }
    });
  }

  private openSendForBiddingDialog(boq: BoQ) {
    const dialogRef = this.dialog.open(SendForBiddingDialogComponent, {
      width: '760px',
      maxWidth: '94vw',
      autoFocus: false,
      data: {
        boq_id: boq.boq_id,
        project_id: boq.project_id,
        boq_code: boq.boq_code,
        boq_name: boq.boq_name,
        project_name: boq.project_name
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success && result?.data?.bidding_link) {
        this.dialog.open(BiddingLinkDialogComponent, {
          width: '560px',
          data: {
            bidding_link: toPublicAppUrl(result.data.bidding_link),
            boq_code: boq.boq_code,
            boq_name: boq.boq_name
          }
        });
      }
    });
  }

  copyBiddingLink(boq: BoQ) {
    (boq as any).copyingLink = true;
    this.apiService.getBiddingInvitationsByBoq(boq.boq_id).subscribe({
      next: (res: any) => {
        (boq as any).copyingLink = false;
        const invitations = (res?.data || []).filter((inv: any) => inv.status !== 'draft');
        const link = toPublicAppUrl(invitations[0]?.bidding_link);
        if (link && navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(link).then(() => {
            this.toastrService.success('Bidding link copied to clipboard');
          }).catch(() => {
            this.toastrService.error('Failed to copy link');
          });
        } else if (!link) {
          this.toastrService.error('No bidding invitation found. Send for Bidding first to create the link.');
        } else {
          this.toastrService.error('Clipboard not available');
        }
      },
      error: (err) => {
        (boq as any).copyingLink = false;
        this.toastrService.error(err.error?.error || 'Failed to fetch bidding link');
      }
    });
  }

  deleteBoQ(boq: BoQ) {
    if (confirm(`Are you sure you want to delete "${boq.boq_name}"?`)) {
      this.apiService.deleteBoQ(boq.boq_id).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.toastrService.success('BoQ deleted successfully');
            this.loadBoQs();
          }
        },
        error: (err: any) => {
          console.error('Error deleting BoQ:', err);
          this.toastrService.error(err.error?.error || 'Failed to delete BoQ');
        }
      });
    }
  }

  clearFilters() {
    this.searchKeyword = '';
    this.selectedStatus = '';
    this.selectedProject = '';
    this.selectedEstimationStatus = '';
    this.assignedToMe = false;
    this.pageIndex = 0;
    this.loadBoQs();
  }

  getEstimationStatusClass(estimationStatus: string): string {
    const classes: { [key: string]: string } = {
      draft: 'bg-gray-100 text-gray-800',
      pending_review: 'bg-amber-100 text-amber-800',
      changes_requested: 'bg-orange-100 text-orange-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return classes[estimationStatus || ''] || 'bg-gray-100 text-gray-800';
  }

  canSendForBidding(boq: BoQ): boolean {
    return (boq.estimation_status || 'draft') === 'approved';
  }

  loadProjects() {
    this.apiService.getProjectCommonAll({ limit: 1000 }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.projects = res.data || [];
        } else {
          this.projects = res.data || res || [];
        }
      },
      error: (err: any) => {
        console.error('Error loading projects:', err);
        this.toastrService.error('Failed to load projects');
      }
    });
  }
}


